import { ActionItem, Card } from '../../../@types/domain';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext } from 'react';
import { GlobalContext } from '../../../context/GlobalState';
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import {
  deleteActionItem,
  updateActionItem,
} from '../../../services/actionItems';
import { errorNoticeDefault } from '../../../utils/notice';

type ActivityItemFormData = Pick<ActionItem, 'text'>;
const schema: SchemaOf<ActivityItemFormData> = yup.object({
  text: yup.string().required().ensure().min(2).label('Work item'),
});

type UpdateActivityItemMutationOptiopns = UseMutationOptions<
  void,
  Error,
  ActivityItemFormData
>;
function useUpdateActivityItemMutation(
  card: Card,
  actionItemIndex: number,
  mutationOptions: UpdateActivityItemMutationOptiopns
) {
  const { retrospective } = useContext(GlobalContext);

  return useMutation<void, Error, ActivityItemFormData>(
    ['actionItems', 'updateActionItem'],
    (data) => {
      return retrospective
        ? updateActionItem(retrospective.id, card, actionItemIndex, data)
        : Promise.reject(new Error('Retrospective not found'));
    },
    mutationOptions
  );
}

export function useRemoveActivityItemMutation(card: Card) {
  const { retrospective } = useContext(GlobalContext);
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>(
    ['actionItems', 'updateActionItem'],
    (actionItemIndex: number) => {
      return retrospective
        ? deleteActionItem(retrospective.id, card, actionItemIndex)
        : Promise.reject(new Error('Retrospective not found'));
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(['getPlatformItemsByIds']);
      },
      onError(error) {
        errorNoticeDefault(error);
      },
    }
  );
}

type UseActivityItemForm = UseFormReturn<ActivityItemFormData> & {
  isLoading: boolean;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
};
export function useActivityItemForm(
  card: Card,
  index: number,
  currentItemText: string | undefined,
  mutationOptions: UpdateActivityItemMutationOptiopns
): UseActivityItemForm {
  const { mutateAsync: updateActivityItemMutate, isLoading: isMutating } =
    useUpdateActivityItemMutation(card, index, mutationOptions);

  const formMethods = useForm<ActivityItemFormData>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      text: currentItemText,
    },
  });

  const onSubmit: SubmitHandler<ActivityItemFormData> = (data) =>
    updateActivityItemMutate(data);

  const { formState } = formMethods;
  const { isSubmitting } = formState;

  const isLoading = isMutating || isSubmitting;

  return {
    ...formMethods,
    isLoading,
    onSubmit: formMethods.handleSubmit(onSubmit),
  };
}
